import React, { forwardRef, useCallback, useEffect } from 'react';
import _ from 'lodash';
import { observer } from 'mobx-react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useQuery } from '@apollo/client';

import useStores from 'shared/hooks/use-stores';
import useTranslation from 'hooks/use-translation';
import {
  CheckoutLabelGrouping,
  CheckoutLabel,
  CheckoutBody,
  CheckoutSectionHeading,
} from 'checkout/components/styles/common';
import RewardsWallet from 'checkout/components/sections/rewards/wallet';
import RewardsSignUp from 'checkout/components/sections/rewards/signup';
import { LoyaltyWidget } from 'checkout/components/sections/loyalty';
import { useGetRewards } from 'src/components/loyalty/use-get-rewards';
import { setLoyaltyData } from 'src/components/loyalty/loyalty.helpers';
import { GetDutchiePosLoyaltyConfigDocument } from 'src/gql/graphql';
import { getShouldShowLoyaltyWidget } from 'src/components/widgets/loyalty-tiers/use-loyalty';

function Rewards({ alpineRewardsEnabled, springbigRewardsEnabled, dutchiePosLoyaltyEnabled }) {
  const flags = useFlags();
  const { t } = useTranslation();
  const { Cart, UI, User } = useStores();
  const { birthday, phone, id: customerId } = User;

  const isDutchiePOSLoyaltyEnabled = Cart.order.dispensary?.consumerDispensaryIntegrations?.dutchieposloyalty?.enabled;
  const isSpringBigFlagEnabled = flags['growth.ecomm.springbig-member-signup.rollout'] ?? false;
  const isSkipSMSSendingEnabled = flags['pos.customers.skip-sms-sending.rollout'] ?? false;
  // Dark launch: defaults off so this new gating behavior only takes effect once explicitly rolled out.
  const isGateOnlineRedemptionWidgetEnabled = flags['pos.ecomm.gate-online-redemption-widget.rollout'] ?? false;
  const onUndoReward = useCallback((rewardId) => Cart.undoApplyReward(rewardId), [Cart]);

  function onApplyReward(rewardId) {
    const award = _.find(Cart.availableRewards, (r) => r.id === rewardId);
    Cart.applyReward(award);
    const { menuTotal, reward } = Cart.costBreakdown;
    // we are intentionally waiting for the costBreakdown to
    // be updated by tax code before checking this
    if (reward > menuTotal) {
      // this would result in a negative subtotal.
      // alert the user and remove the reward
      UI.showErnie(
        t(
          'rewards.exceededCartWarning',
          'You cannot use a reward that exceeds your cart total. Please add more items to your order and try again.'
        )
      );
      onUndoReward(rewardId);
    }
  }

  useEffect(() => {
    // if a dispensary disables a reward that a user has applied to their cart
    // and they refresh the page, the reward will disappear and put the user in a
    // state where they can't checkout. This checks for that and removes the reward automatically.
    _.forEach(Cart.order.appliedRewards, (appliedReward) => {
      const availableReward = _.find(Cart.availableRewards, { id: appliedReward.id });
      if (!availableReward) {
        onUndoReward(appliedReward.id);
      }
    });
  }, [Cart.availableRewards, Cart.order.appliedRewards, onUndoReward]);

  const rewardsToShow = _.filter(Cart.availableRewards, [`available`, true]);
  const hasEligibleRewards = _.some(Cart.availableRewards, [`available`, true]);

  const hasDiscount = Cart.hasDiscounts;

  const skipForDutchiePOS = !dutchiePosLoyaltyEnabled || !customerId || !birthday || !phone || User.isLoyaltyMember;
  const skipForSpringbig = !springbigRewardsEnabled || !customerId || !birthday || !phone || Cart.hasCheckedForRewards;
  const skipForAlpine = !alpineRewardsEnabled || !customerId || !birthday || !phone || Cart.hasCheckedForRewards;
  const skip = skipForDutchiePOS && skipForSpringbig && skipForAlpine;
  // Widget-visibility config query must NOT skip for enrolled loyalty members (unlike
  // skipForDutchiePOS above, which is scoped to whether rewards still need to be fetched) —
  // otherwise showLoyaltyWidgetOnWeb always evaluates false for them.
  const skipForDutchiePOSLoyaltyConfig = !dutchiePosLoyaltyEnabled || !customerId || !birthday || !phone;

  const { data, loading: rewardsLoading } = useGetRewards({
    dispensaryId: Cart.order.dispensary.id,
    birthday,
    pin: isSkipSMSSendingEnabled ? 'skip' : '',
    phone,
    skip,
    customerId,
  });

  const { data: dposConfigData } = useQuery(GetDutchiePosLoyaltyConfigDocument, {
    variables: {
      dispensaryId: Cart.order.dispensary.id,
    },
    skip: skipForDutchiePOSLoyaltyConfig,
  });

  useEffect(() => {
    if (!rewardsLoading && data?.getRewardsV2) {
      setLoyaltyData({
        user: User,
        cart: Cart,
        data,
        dispensaryId: Cart.order.dispensary.id,
      });
    }
  }, [User, Cart, data, rewardsLoading, data?.getRewardsV2]);

  const hasRewards =
    Cart.hasRewardsWallet ||
    User.isLoyaltyMember ||
    (dutchiePosLoyaltyEnabled && !dposConfigData?.getDutchiePosLoyaltyConfig?.loyaltyRequireOptIn);

  const showLoyaltyWidgetOnWeb = getShouldShowLoyaltyWidget({
    loyaltyConfig: dposConfigData?.getDutchiePosLoyaltyConfig,
    isLoyaltyMember: User.isLoyaltyMember,
  });

  // "Allow customers to redeem online" (BOH) is a master switch for the whole widget,
  // distinct from "Show loyalty widget" (loyaltyWidgetEnabledOnWeb, above). When a merchant
  // disables it, the widget must not render at all — unset/nil defaults to allowed for
  // dispensaries that haven't touched this newer setting.
  const isOnlineRedemptionEnabled = isGateOnlineRedemptionWidgetEnabled
    ? dposConfigData?.getDutchiePosLoyaltyConfig?.onlineRedemptionEnabledOnWeb ?? true
    : true;

  const showSignup =
    !hasRewards &&
    (alpineRewardsEnabled || (springbigRewardsEnabled && isSpringBigFlagEnabled) || dutchiePosLoyaltyEnabled);

  if (rewardsLoading) {
    return null;
  }
  if (!hasRewards && !showSignup) {
    return null;
  }
  if (Cart.rewardsBalance === '') {
    return null;
  }

  return (
    <CheckoutLabelGrouping id='rewards'>
      <CheckoutLabel>
        <CheckoutSectionHeading id='rewardsSectionHeading'>{Cart.rewardsProgramDisplayName}</CheckoutSectionHeading>
      </CheckoutLabel>

      <CheckoutBody>
        {hasRewards && !isDutchiePOSLoyaltyEnabled && (
          <RewardsWallet
            appliedRewards={Cart.order.appliedRewards}
            combineDiscounts={!isDutchiePOSLoyaltyEnabled && Cart.combineDiscounts}
            hasAppliedAnyRewards={!_.isEmpty(Cart.order.appliedRewards)}
            hasCoupon={!_.isEmpty(Cart.coupon)}
            hasDiscount={hasDiscount}
            hasEligibleRewards={hasEligibleRewards}
            onApplyReward={onApplyReward}
            onUndoReward={onUndoReward}
            rewardsBalance={Cart.rewardsBalance}
            rewardsProgramDisplayName={Cart.rewardsProgramDisplayName}
            rewardsToShow={rewardsToShow}
            rewardsType={Cart.rewardsType}
            isDutchiePOSLoyaltyEnabled={isDutchiePOSLoyaltyEnabled}
          />
        )}

        {showSignup && (
          <RewardsSignUp
            alpineRewardsEnabled={alpineRewardsEnabled}
            springbigRewardsEnabled={springbigRewardsEnabled}
            dutchiePosLoyaltyEnabled={dutchiePosLoyaltyEnabled}
          />
        )}

        {!showSignup && isDutchiePOSLoyaltyEnabled && showLoyaltyWidgetOnWeb && isOnlineRedemptionEnabled && (
          <LoyaltyWidget />
        )}
      </CheckoutBody>
    </CheckoutLabelGrouping>
  );
}

export default observer(forwardRef(Rewards));
